import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useOrg } from '@/context/OrgContext';

export function OrgRoute({ children, requiredRole = 'viewer', requiredPermission = null }) {
  const { user, loading: authLoading } = useAuth();
  const { memberRole, loading: orgLoading, organization, can } = useOrg();

  // Show loading while checking auth and org
  if (authLoading || orgLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect to org overview if no organization
  if (!organization) {
    return <Navigate to="/org/overview" />;
  }

  // Check role hierarchy: owner > admin > member > viewer
  const roleHierarchy = {
    owner: 4,
    admin: 3,
    member: 2,
    viewer: 1
  };

  const userRoleLevel = roleHierarchy[memberRole] || 0;
  const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

  // Check if user has sufficient role
  if (userRoleLevel < requiredRoleLevel) {
    // Redirect to overview with insufficient permissions
    return <Navigate to="/org/overview" />;
  }

  // Check specific permission if required
  if (requiredPermission && !can(requiredPermission)) {
    return <Navigate to="/org/overview" />;
  }

  // Use children if provided (for direct usage), otherwise use Outlet (for nested routes)
  return children ? children : <Outlet />;
}

export function OrgAdminRoute({ children }) {
  return <OrgRoute requiredRole="admin">{children}</OrgRoute>;
}

export function OrgOwnerRoute({ children }) {
  return <OrgRoute requiredRole="owner">{children}</OrgRoute>;
}
